import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import logger from '@/utils/logger';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

// Token storage via refs to avoid stale closures
const tokenRef = { access: null, refresh: null };

api.interceptors.request.use((config) => {
  if (tokenRef.access) config.headers.Authorization = `Bearer ${tokenRef.access}`;
  return config;
});

function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  return detail?.msg || String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (!tokenRef.access) { setUser(false); setLoading(false); return; }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (err) {
      logger.error('Auth check failed:', err.message);
      tokenRef.access = null;
      tokenRef.refresh = null;
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data.access_token) { tokenRef.access = data.access_token; tokenRef.refresh = data.refresh_token; }
      setUser({ id: data.id, name: data.name, email: data.email, role: data.role });
      return { success: true };
    } catch (e) {
      logger.error('Login failed:', e.message);
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  }, []);

  const register = useCallback(async (name, email, password) => {
    try {
      const { data } = await api.post('/auth/register', { name, email, password });
      if (data.access_token) { tokenRef.access = data.access_token; tokenRef.refresh = data.refresh_token; }
      setUser({ id: data.id, name: data.name, email: data.email, role: data.role });
      return { success: true };
    } catch (e) {
      logger.error('Registration failed:', e.message);
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); }
    catch (err) { logger.error('Logout error:', err.message); }
    tokenRef.access = null;
    tokenRef.refresh = null;
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, loading, login, register, logout, api }), [user, loading, login, register, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() { return useContext(AuthContext); }
export { api };
